import {
    AsyncPipe,
    isPlatformBrowser,
    LowerCasePipe,
    NgTemplateOutlet,
} from "@angular/common";
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    HostListener,
    Inject,
    PLATFORM_ID,
    Signal,
} from "@angular/core";
import {
    Router,
    RouterLink,
    RouterLinkActive,
    RouterModule,
} from "@angular/router";
import { RegistrationAnalyticInitSources } from "@Front-end/seed/modules/registration-v2";
import { UserDirective } from "@Front-end/seed/modules/user";
import { BannerV2Directive } from "@Front-end/seed/modules/v2/banners-v2";
import { LuckyWheelStoreService } from "@Front-end/seed/modules/v2/lucky-wheel";
import { NgLetDirective } from "@Front-end/ui/directives/ng-let";
import { ModalOpenType } from "@Front-end/ui/models/modal";
import {
    ButtonV3KindDirective,
    ButtonV3PaletteDirective,
    ButtonV3SizeDirective,
} from "@Front-end/ui/modules/button-v3";
import { IconComponent } from "@Front-end/ui/modules/icon";
import {
    LocalizeRouterModule,
    LocalizeRouterService,
} from "@gilsdav/ngx-translate-router";
import { TranslateModule } from "@ngx-translate/core";
import { environment } from "../../../environments/environment";
import {
    FeatureFlags,
    hasFeatureFlag,
} from "../../constants/feature-flags.constants";
import { minLaptopWidth } from "../../constants/media.constants";
import { LangObj } from "../../models/lang.model";
import { LivechatButtonDirective } from "../../modules/live-chat/livechat-button.directive";
import { PictureComponent } from "../../modules/shared/picture/picture.component";
import { SidePanelManagerService } from "../../services/side-panel-manager.service";
import { AppPopupsService } from "../../store/services/app-popups.service";
import { ChooseLangsDirective } from "../choose-langs/directives/choose-langs.directive";
import { NewFeatureBannerComponent } from "../new-feature-banner/new-feature-banner.component";
import { ProfileInfoComponent } from "./profile-info/profile-info.component";
import { SectionBoxComponent } from "./section-box/section-box.component";
import { SpecialMenuItemComponent } from "./special-menu-item/special-menu-item.component";

@Component({
    selector: "ls-menu",
    templateUrl: "./menu.component.html",
    styleUrls: ["./menu.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        RouterModule,
        RouterLink,
        RouterLinkActive,
        TranslateModule,
        LocalizeRouterModule,
        ProfileInfoComponent,
        AsyncPipe,
        UserDirective,
        ButtonV3KindDirective,
        ButtonV3PaletteDirective,
        ButtonV3SizeDirective,
        NgLetDirective,
        IconComponent,
        PictureComponent,
        SectionBoxComponent,
        NgTemplateOutlet,
        ChooseLangsDirective,
        SpecialMenuItemComponent,
        LivechatButtonDirective,
        LowerCasePipe,
        BannerV2Directive,
        NewFeatureBannerComponent,
    ],
    // eslint-disable-next-line @angular-eslint/no-host-metadata-property
    host: {
        "class": "menu",
        // eslint-disable-next-line @angular-eslint/no-host-metadata-property,@typescript-eslint/naming-convention
        "[class._visible]": "isOpen()",
    },
})
export class MenuComponent {

    constructor(
        private cdr: ChangeDetectorRef,
        private appPopupsService: AppPopupsService,
        private sidePanelManagerService: SidePanelManagerService,
        private localize: LocalizeRouterService,
        private router: Router,
        private luckyWheelStoreService: LuckyWheelStoreService,
        @Inject(PLATFORM_ID) private platformId: Object,
    ) {
    }

    public currentLanguage: LangObj | undefined;
    public isMobile: boolean = isPlatformBrowser(this.platformId) ? screen.width < minLaptopWidth : true;
    public hasLuckyWin: boolean = hasFeatureFlag(FeatureFlags.LUCKY_WIN);
    public cdnUrl: string = environment.cdnUrl;
    public minioUrl: string = environment.minioUrl;
    public eventTheme: string = environment.eventTheme;
    public isOpen: Signal<boolean> = this.sidePanelManagerService.isMenuOpen;
    public hasTournaments: boolean = hasFeatureFlag(FeatureFlags.TOURNAMENTS);
    public hasLotteries: boolean = hasFeatureFlag(FeatureFlags.LOTTERIES);
    public hasAchievements: boolean = hasFeatureFlag(FeatureFlags.ACHIEVEMENTS);
    public showSport: boolean = hasFeatureFlag(FeatureFlags.SPORT);
    public linksListOne = [
        {
            name: "common.category-casino",
            url: "/game-categories/popular",
            icon: "cards",
        },
        {
            name: "common.category-live-casino",
            url: "/game-categories/live-casino",
            icon: "flash",
        },
        this.showSport
            ? {
                name: "common.category-sport",
                url: "/sport",
                icon: "sports",
            }
            : undefined,
    ];
    public linksListTwo = [
        {
            name: "common.missions",
            url: "/missions",
            icon: "target",
            label: "common.new",
        },
        {
            name: "common.category-vip",
            url: "/vip",
            icon: "crown",
        },
        this.hasLotteries
            ? {
                name: "common.lotteries",
                url: "/lotteries",
                icon: "ticket",
                label: "common.new",
            } : undefined,
        {
            name: "common.category-shop",
            url: "/shop",
            icon: "coin",
        },
        {
            name: "common.category-promotions",
            url: "/promotions",
            icon: "gift",
        },
    ];

    @HostListener("click", ["$event"])
    public clickOutsideSidebar(event: Event): void {
        const clickOutSidebar = !(event.target as HTMLElement).closest(".menu__sidebar");
        if (clickOutSidebar) {
            this.closeBurger();
        }
    }

    public closeBurger(): void {
        this.togglePanel(false);
        this.cdr.markForCheck();
    }

    public closeMenu(): void {
        this.sidePanelManagerService.closeMenu();
    }

    public closeRedirect(url: string): void {
        void this.router.navigate([this.localize.translateRoute(url)]);
        this.sidePanelManagerService.closeMenu();
    }

    public togglePanel(isOpen: boolean): void {
        if (isOpen) {
            this.sidePanelManagerService.openMenu();
        } else {
            this.sidePanelManagerService.close();
        }
    }

    public openWheel(): void {
        this.appPopupsService.fireOpenLuckyWheel(ModalOpenType.MANUAL);
        this.closeBurger();
    }

    public openRegistration(): void {
        this.appPopupsService.fireOpenRegistration(
            { source: RegistrationAnalyticInitSources.LUCKY_WIN_ATTEMPT },
            ModalOpenType.MANUAL,
        );
        this.closeBurger();
    }

    public onLanguageChange(language: LangObj): void {
        this.currentLanguage = language;
    }
}
